import { useParams, useNavigate } from "react-router-dom";
import { StickyNote, Pencil, Trash2 } from "lucide-react";
import Button from "./ButtonComponent";
import ChatWidget from "./ChatWidget";
import HealthMetricsDashboard from "./HealthMetricsDashboard";
import { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { API_BASE_URLS, PATIENT_DETAILS } from "../constants/api";
import type { Patient } from "../types";
import { auth, apiUtils } from "../utils";

export default function PatientDetails() {
  // Tab state and tabs array must be at the top
  const [selectedTab, setSelectedTab] = useState("Health Metrics");
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aiSummary, setAiSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const tabs = ["Health Metrics", "Medical History", "Notes", "Reports"];

  const { id } = useParams();
  const navigate = useNavigate();
  const doctorId = "dr_gupta";

  const aiCalledRef = useRef<string | null>(null);
  const [notes, setNotes] = useState<
    { text: string; date: Date; id?: string; updatedAt?: Date }[]
  >([]);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  const [showAiSummarySidebar, setShowAiSummarySidebar] = useState(false);

  // Disable body scroll when sidebar is open
  useEffect(() => {
    if (showAiSummarySidebar) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showAiSummarySidebar]);
  const [reports, setReports] = useState<any>(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [reportAnalysis, setReportAnalysis] = useState<any>({});
  const [reportSortOrder, setReportSortOrder] = useState<"newest" | "oldest">(
    "newest"
  );
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Health Metrics Dashboard state
  const [heartRateDataForChart, setHeartRateDataForChart] = useState<number[]>(
    []
  );
  const [bloodPressureDataForChart, setBloodPressureDataForChart] = useState<
    [number, number][]
  >([]);
  const [bloodOxygenDataForChart, setBloodOxygenDataForChart] = useState<
    number[]
  >([]);
  const [heartRateTimeLabels, setHeartRateTimeLabels] = useState<string[]>([]);
  const [bloodPressureTimeLabels, setBloodPressureTimeLabels] = useState<
    string[]
  >([]);
  const [bloodOxygenTimeLabels, setBloodOxygenTimeLabels] = useState<string[]>(
    []
  );
  const [latestHeartRate, setLatestHeartRate] = useState<number | null>(null);
  const [latestBloodPressure, setLatestBloodPressure] = useState<{
    systolic: number;
    diastolic: number;
  } | null>(null);
  const [latestBloodOxygen, setLatestBloodOxygen] = useState<number | null>(
    null
  );
  const [latestHeartRateTime, setLatestHeartRateTime] = useState("");
  const [latestBloodPressureTime, setLatestBloodPressureTime] = useState("");
  const [latestBloodOxygenTime, setLatestBloodOxygenTime] = useState("");

  // Helper function to truncate HTML text to 4 lines
  const truncateToFourLines = (text: string) => {
    // Remove HTML tags for line counting
    const textWithoutTags = text.replace(/<[^>]*>/g, "");
    const lines = textWithoutTags.split("\n");

    // Show only 4 lines
    const targetLines = 4;

    if (lines.length <= targetLines) return text;

    // Count actual text lines (excluding HTML tags)
    let lineCount = 0;
    let result = "";

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (char === "\n") {
        lineCount++;
        result += char;
        if (lineCount >= targetLines) {
          result += "...";
          break;
        }
      } else {
        result += char;
      }
    }

    return result;
  };

  useEffect(() => {
    const fetchPatient = async () => {
      setLoading(true);
      setError(null);
      try {
        const requestOptions = apiUtils.createRequestOptions("GET");
        const response = await fetch(`${PATIENT_DETAILS}/${id}`, requestOptions);
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || "Failed to fetch patient details");
        }
        const result = await response.json();
        // The API returns the same structure as the list endpoint
        setPatient(result);
      } catch (err: any) {
        setError(err.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    };
    fetchPatient();
  }, [id]);

  // Fetch reports when patient loads
  useEffect(() => {
    if (!patient?.userId) return;

    const fetchReports = async () => {
      setReportsLoading(true);
      setReportsError(null);
      try {
        const requestOptions = apiUtils.createRequestOptions("GET");
        const response = await fetch(
          `${API_BASE_URLS.HELIUS_SERVICE}/reports/${patient.userId}`,
          requestOptions
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || "Failed to fetch reports");
        }
        const result = await response.json();
        setReports(result.reports);

        // Extract analysis data from the reports response
        const analysisData: any = {};
        for (const [reportType, reportList] of Object.entries(result.reports)) {
          if (Array.isArray(reportList) && reportList.length > 0) {
            for (const report of reportList) {
              // Check if the report has a data object (like lipid_profile.data)
              if (report.data) {
                analysisData[`${reportType}_${report._id}`] = {
                  ...report.data,
                  createdAt: report.createdAt,
                };
              }
            }
          }
        }
        setReportAnalysis(analysisData);
      } catch (err: any) {
        setReportsError(err.message || "Failed to fetch reports");
      } finally {
        setReportsLoading(false);
      }
    };

    fetchReports();
  }, [patient?.userId]);

  useEffect(() => {
    if (!patient || aiCalledRef.current === patient.userId) return;
    // Wait for reports to be loaded before generating AI summary
    if (reportsLoading || !reports) return;
    aiCalledRef.current = patient.userId;

    const formatHealthQuestionnaire = (responses: any[]): string => {
      if (!Array.isArray(responses)) return "";
      const lines: string[] = [];
      responses.forEach((resp: any) => {
        if (Array.isArray(resp.answers)) {
          resp.answers.forEach((ans: any) => {
            if (ans.label && ans.value) {
              lines.push(`- ${ans.label}: ${ans.value}`);
            }
          });
        }
      });
      return lines.join("\n");
    };

    const fetchAISummary = async () => {
      setAiLoading(true);
      setAiError(null);
      try {
        // Helper function to get latest vital from patient.vitals
        const getLatestVital = (bodyType: string) => {
          if (!patient.vitals) return null;
          let all: any[] = [];
          Object.values(patient.vitals).forEach((arr: any) => {
            if (Array.isArray(arr)) {
              all = all.concat(arr.filter((v: any) => v.bodyType === bodyType));
            }
          });
          if (all.length === 0) return null;
          all.sort(
            (a: any, b: any) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          );
          return all[0];
        };

        // Get latest vitals from patient API response
        const latestHeartRateVital = getLatestVital("HEART_RATE");
        const latestBloodPressureVital = getLatestVital("BLOOD_PRESSURE");
        const latestBloodOxygenVital = getLatestVital("BLOOD_OXYGEN");

        // Extract values
        const latestHeartRate = latestHeartRateVital?.body?.value || "N/A";
        const latestBloodPressure = latestBloodPressureVital?.body
          ? `${latestBloodPressureVital.body.systolic}/${latestBloodPressureVital.body.diastolic}`
          : "N/A";
        const latestBloodOxygen = latestBloodOxygenVital?.body?.value || "N/A";

        const prompt = `
           Patient risk level: ${patient.score_summary?.risk_level}
           Health vitals: ${JSON.stringify(patient.score_summary?.inputSummary)}
           Latest Vitals from API:
           - Heart Rate: ${latestHeartRate} bpm
           - Blood Pressure: ${latestBloodPressure} mmHg
           - Blood Oxygen: ${latestBloodOxygen}%
           Health Questionnaire:\n${formatHealthQuestionnaire(
             patient?.healthQuestionnaireResponse || []
           )}
           Report Analysis Data: ${JSON.stringify(reportAnalysis)}

You are a cardiologist-style clinical reviewer of integrated patient data (wearable + symptom input + health questionnaire + labs + ECG summaries). Produce a concise, clinically useful summary in HTML only. The summary must be ready for another cardiologist to read quickly — put all truly critical items first for immediate recognition.

Hard rules (must follow exactly):
1. Output only valid HTML (no surrounding commentary). Use the exact fixed sections below in the same order.
2. At the very top include a single-line visually prominent "Top Critical Alerts" row. For each patient, analyze and include the Chief Complaints and Health Conditions provided in Health Questionnaire, along with vitals, labs, and ECG findings. Include only items that are clinically high-risk or red-flag concerns. Each alert must appear as: <span style="color: #ff0000; font-weight: bold;"></span>. If none, show: <span style="color: #006400; font-weight: 600;">No critical alerts</span>.
3. In the body, highlight any critical item (vital, lab, ECG abnormality, or red-flag symptom) using exactly: <span style="color: #ff0000; font-weight: bold;">text</span>.
4. Keep the full fixed structure below (section headings styled like the example). Keep language clinical and data-driven. Be concise — each section 1–4 short sentences or a bordered table when specified.
5. Labs table: include only valid lab results. Use a bordered table showing: Test | Value | Unit | Ref Range | Trend. If ≥2 results exist for a test, add a second row or a small inline comparison "Latest vs Previous: X → Y (Δ, %)" in the same cell. Highlight critical values in the table with the red-bold span. Use “--” for unknown ref ranges.
6. Risk Stratification: use Low / Medium / High (state what main driver(s) produced this classification in one short phrase).
7. Provisional Diagnosis: data-driven only (no speculation). Max one line.
8. Advice & Clinical Plan: give 3 clear items: (1) immediate red-flag actions if any, (2) recommended investigations, (3) follow-up/referral — all in non-directive phrasing.
9. Do not include Mongo IDs, raw questionnaire metadata, or extraneous debug text.

Output structure (HTML — preserve heading style below and exact section order). Put the Top Critical Alerts row before Patient Demographics:

<div style="font-weight: 700; font-size:16px; margin-top:12px; margin-bottom:8px;">Top Critical Alerts:</div>
[Single-line comma-separated alerts OR No critical alerts — use red-bold span for alerts]

<div style="font-weight: 600; color: #000000; margin-top: 16px; margin-bottom: 8px;">Patient Demographics:</div>
Age, gender, ABHA ID (if available), known history (one short line)

<div style="font-weight: 600; color: #000000; margin-top: 16px; margin-bottom: 8px;">Vital Signs:</div>
HR (avg / range), BP (latest / avg with date/time), SpO₂, RR (if available), weight/BMI, wearable trends (one short line). Highlight out-of-range values.

<div style="font-weight: 600; color: #000000; margin-top: 16px; margin-bottom: 8px;">Clinical Presentation:</div>
Chief complaint, duration, associated symptoms, severity (1–3 short bullets or one short paragraph). Flag red-flag symptoms using red-bold span.

<div style="font-weight: 600; color: #000000; margin-top: 16px; margin-bottom: 8px;">Risk Factors:</div>
Lifestyle, comorbidities (HTN, DM, dyslipidemia), smoking/tobacco, family history (concise list).

<div style="font-weight: 600; color: #000000; margin-top: 16px; margin-bottom: 8px;">Laboratory Investigations:</div>
Show only valid labs. Provide a bordered HTML table: columns = Test | Value | Unit | Ref Range | Trend. Include Latest vs Previous (Δ, % change) when ≥2 results. Highlight critical/danger values in red-bold span. Tests of interest: CBC, CRP, HbA1c, Troponin I, Lipid profile. If test missing, omit row.

<div style="font-weight: 600; color: #000000; margin-top: 16px; margin-bottom: 8px;">ECG Findings:</div>
Chronological ECG summaries (date/time) with succinct interpretation. Flag abnormalities (ischemia, new ST changes, pathologic Q, AF, high-grade block, prolonged QT) — highlight flagged items in red-bold span.

<div style="font-weight: 600; color: #000000; margin-top: 16px; margin-bottom: 8px;">Provisional Diagnosis:</div>
Single-line, data-driven only.

<div style="font-weight: 600; color: #000000; margin-top: 16px; margin-bottom: 8px;">Advice & Clinical Plan:</div>
Three concise actionable items: 1) immediate actions (if any), 2) recommended tests, 3) follow-up/referral suggestions (non-directive phrasing). Use short bullets.

<div style="font-weight: 600; color: #000000; margin-top: 16px; margin-bottom: 8px;">Risk Stratification:</div>
Low / Medium / High. State primary driver(s) in parentheses.

Presentation & tone:
• Use medical language suited for cardiology.  
• Keep each section brief — the whole HTML output should fit on one readable screen if possible.  
• Where you highlight critical values, also include a one-line clinical implication (e.g., "Troponin I elevated → probable acute myocardial injury").  
• No extraneous explanations, no API keys, no raw IDs, no long narrations.

Example of critical highlight usage (must be followed exactly):
<span style="color: #ff0000; font-weight: bold;">Troponin I 0.85 ng/mL (elevated)</span>

Now produce the summary in the exact HTML structure above for the provided patient data. If no patient data provided, output an example template with placeholder text (clearly labelled) using the same structure.

         `;
        const geminiApiKey = process.env.REACT_APP_GEMINI_API_KEY;

        if (!geminiApiKey) {
          console.error("Missing Gemini API key.");
          setAiError("AI summary is unavailable.");
          setAiLoading(false);
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_HELIUS_SERVICE_URL}/get-user-medical-summary`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include", // Include cookies for authentication
            body: JSON.stringify({ prompt }),
          }
        );
        const data = await response.json();
        const rawText = data?.data || "No summary available.";

        // Extract HTML content from markdown code blocks if present
        let htmlContent = rawText;
        if (rawText.includes("```html")) {
          const htmlMatch = rawText.match(/```html\s*([\s\S]*?)\s*```/);
          if (htmlMatch) {
            htmlContent = htmlMatch[1].trim();
          }
        } else if (rawText.includes("```")) {
          // Handle generic code blocks
          const codeMatch = rawText.match(/```\s*([\s\S]*?)\s*```/);
          if (codeMatch) {
            htmlContent = codeMatch[1].trim();
          }
        }

        setAiSummary(htmlContent);
      } catch (err) {
        setAiError("Failed to fetch AI summary.");
      } finally {
        setAiLoading(false);
      }
    };

    fetchAISummary();
    // Do not return anything from useEffect
    // eslint-disable-next-line
  }, [patient, reportAnalysis, reportsLoading, reports]);

  // Load notes from Firebase when patient loads
  useEffect(() => {
    if (!patient?.userId) return;

    const notesQuery = query(
      collection(db, "patients", patient.userId, "notes"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(notesQuery, (snapshot) => {
      const loadedNotes = snapshot.docs.map((doc) => ({
        id: doc.id,
        text: doc.data().text,
        date: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || undefined,
      }));
      setNotes(loadedNotes);
    });

    return () => unsubscribe();
  }, [patient?.userId]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowSortDropdown(false);
      }
    };

    if (showSortDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSortDropdown]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading patient details...
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Patient Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The patient you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate("/")}>Back to Home</Button>
        </div>
      </div>
    );
  }

  // Risk badge color using Tailwind RAG colors
  const riskLevel = patient.score_summary?.risk_level || "poor";
  const riskLevelNormalized = riskLevel.toLowerCase();

  // Get badge color and display text
  let riskBadge = "bg-gray-300 text-gray-700";
  let riskDisplayText =
    riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1).toLowerCase();

  if (riskLevelNormalized === "good") {
    riskBadge = "bg-rag-green text-white";
    riskDisplayText = "Good";
  } else if (riskLevelNormalized === "average") {
    riskBadge = "bg-rag-amber text-white";
    riskDisplayText = "Average";
  } else if (riskLevelNormalized === "poor") {
    riskBadge = "bg-rag-red text-white";
    riskDisplayText = "Poor";
  }

  // Patient details from new structure
  const fullName = patient.personal_information?.full_name || "-";
  const genderRaw = patient.personal_information?.gender || "-";
  const gender =
    genderRaw.charAt(0).toUpperCase() + genderRaw.slice(1).toLowerCase();
  const dob = patient.personal_information?.dob || "-";
  const phoneRaw = patient.contact_information?.phone || "-";
  const phone = `+91 ${phoneRaw}`;
  const height = patient.physical_details?.height
    ? `${patient.physical_details.height} cm`
    : "-";
  const bloodGroup = patient.physical_details?.blood_group || "A+";
  // Calculate age from dob
  const getAge = (dob: string) => {
    if (!dob || dob === "-") return "-";

    let day: number, month: number, year: number;

    // Check if it's yyyy-mm-dd format
    if (dob.includes("-")) {
      const parts = dob.split("-");
      if (parts.length === 3) {
        year = parseInt(parts[0]);
        month = parseInt(parts[1]);
        day = parseInt(parts[2]);
      } else {
        return "-";
      }
    } else {
      // Handle dd/mm/yyyy format
      const parts = dob.split("/");
      if (parts.length === 3) {
        day = parseInt(parts[0]);
        month = parseInt(parts[1]);
        year = parseInt(parts[2]);
      } else {
        return "-";
      }
    }

    if (!year) return "-";
    const today = new Date();
    let age = today.getFullYear() - year;
    if (
      today.getMonth() + 1 < month ||
      (today.getMonth() + 1 === month && today.getDate() < day)
    ) {
      age--;
    }
    return age;
  };
  const age = getAge(dob);

  // Helper functions for medical history
  function getQuestionnaireList(questionId: string) {
    const entry = patient?.healthQuestionnaireResponse?.find((q: any) =>
      q.answers?.some((a: any) => a.questionId === questionId)
    );
    const formatValue = (val: string) => {
      if (!val) return "";
      const noUnderscore = val.replace(/_/g, " ");
      return noUnderscore.charAt(0).toUpperCase() + noUnderscore.slice(1);
    };
    if (entry) {
      // Find all answers for this questionId
      const answers = entry.answers.filter(
        (a: any) => a.questionId === questionId
      );
      // For symptoms and health_conditions, value is inside data array
      if (
        ["symptoms", "health_conditions", "medications"].includes(questionId)
      ) {
        let values: string[] = [];
        answers.forEach((a: any) => {
          if (Array.isArray(a.data)) {
            values = values.concat(
              a.data.map((d: any) => formatValue(d.value))
            );
          }
        });
        const result = values.join(", ");
        // Special handling for symptoms: return empty string if value is "none" or "None"
        if (result.toLowerCase() === "none") {
          return "";
        }
        return result;
      } else {
        // For other questionIds, value is direct
        return answers.map((a: any) => formatValue(a.value)).join(", ");
      }
    }
    return "";
  }
  function getQuestionnaireValue(questionId: string) {
    const entry = patient?.healthQuestionnaireResponse?.find((q: any) =>
      q.answers?.some((a: any) => a.questionId === questionId)
    );
    if (entry) {
      const answer = entry.answers.find(
        (a: any) => a.questionId === questionId
      );
      if (questionId === "diabetes") {
        const diabetesType = answer?.value;
        if (diabetesType === "type_1" || diabetesType === "type1")
          return "Diabetes type 1";
        if (diabetesType === "type_2" || diabetesType === "type2")
          return "Diabetes type 2";
        return "";
      }
      return answer?.value || "";
    }
    return "";
  }

  // Save note to Firebase (handles both create and update)
  const handleSaveNote = async () => {
    const noteText = editingNoteId ? editingNoteText : newNote;
    if (!noteText.trim() || !patient?.userId) return;

    try {
      if (editingNoteId) {
        // Update existing note
        const noteRef = doc(
          db,
          "patients",
          patient.userId,
          "notes",
          editingNoteId
        );
        await updateDoc(noteRef, {
          text: noteText.trim(),
          updatedAt: serverTimestamp(),
        });
        setEditingNoteId(null);
        setEditingNoteText("");
      } else {
        // Create new note
        await addDoc(collection(db, "patients", patient.userId, "notes"), {
          text: noteText.trim(),
          createdAt: serverTimestamp(),
          doctorId: doctorId,
        });
        setNewNote("");
      }

      setShowNoteModal(false);
    } catch (error) {
      console.error("Error saving note:", error);
      // You might want to show an error message to the user
    }
  };

  // Handle edit note
  const handleEditNote = (noteId: string, noteText: string) => {
    setEditingNoteId(noteId);
    setEditingNoteText(noteText);
    setShowNoteModal(true);
  };

  // Handle delete note (show confirmation)
  const handleDeleteNote = (noteId: string) => {
    setNoteToDelete(noteId);
  };

  // Confirm and delete note
  const confirmDeleteNote = async () => {
    if (!noteToDelete || !patient?.userId) return;

    try {
      const noteRef = doc(
        db,
        "patients",
        patient.userId,
        "notes",
        noteToDelete
      );
      await deleteDoc(noteRef);
      setNoteToDelete(null);
    } catch (error) {
      console.error("Error deleting note:", error);
      // You might want to show an error message to the user
    }
  };

  // Handle report click to fetch and display PDF
  const handleReportClick = async (reportType: string, fileName: string) => {
    if (!patient?.userId) return;

    setPdfLoading(true);
    setShowPdfModal(true);
    setPdfUrl(null);

    try {
      const requestOptions = apiUtils.createRequestOptions("POST", {
        reportType: reportType,
        fileName: fileName,
      });
      const response = await fetch(
        `${API_BASE_URLS.HELIUS_SERVICE}/get-report/${patient.userId}`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error("Failed to fetch PDF");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      console.error("Error fetching PDF:", error);
      // You might want to show an error message to the user
    } finally {
      setPdfLoading(false);
    }
  };

  // Close PDF modal and cleanup
  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  // Helper function to format lipid profile data with color coding
  const formatLipidProfileData = (data: any) => {
    if (!data) return null;

    const { total_cholesterol, ldl_cholesterol, hdl_cholesterol, hdl_ratio } =
      data;

    const getColorClass = (value: string, type: string) => {
      const numValue = parseFloat(value);
      switch (type) {
        case "hdl":
          return numValue >= 50 ? "text-green-600" : "text-red-600";
        case "ldl":
          return numValue < 70 ? "text-green-600" : "text-red-600";
        case "hdl_ratio":
          return numValue < 4 ? "text-green-600" : "text-red-600";
        case "total_cholesterol":
          return numValue < 200 ? "text-green-600" : "text-red-600";
        default:
          return "text-gray-600";
      }
    };

    return (
      <span className="text-sm">
        Cholesterol total:{" "}
        <span className={getColorClass(total_cholesterol, "total_cholesterol")}>
          {total_cholesterol}
        </span>
        , LDL:{" "}
        <span className={getColorClass(ldl_cholesterol, "ldl")}>
          {ldl_cholesterol}
        </span>
        , HDL:{" "}
        <span className={getColorClass(hdl_cholesterol, "hdl")}>
          {hdl_cholesterol}
        </span>
        , HDL ratio:{" "}
        <span className={getColorClass(hdl_ratio, "hdl_ratio")}>
          {hdl_ratio}
        </span>
      </span>
    );
  };

  // Helper function to format CBC data with color coding
  const formatCBCData = (data: any) => {
    if (!data) return null;

    const { hemoglobin_g_dl, wbc_per_ul, platelets_per_ul } = data;

    const getColorClass = (value: string, type: string) => {
      const numValue = parseFloat(value);
      switch (type) {
        case "hemoglobin":
          return numValue >= 12 && numValue <= 15
            ? "text-green-600"
            : "text-red-600";
        case "wbc":
          return numValue >= 4000 && numValue <= 10000
            ? "text-green-600"
            : "text-red-600";
        case "platelets":
          return numValue >= 150000 && numValue <= 410000
            ? "text-green-600"
            : "text-red-600";
        default:
          return "text-gray-600";
      }
    };

    return (
      <span className="text-sm">
        Hemoglobin:{" "}
        <span className={getColorClass(hemoglobin_g_dl, "hemoglobin")}>
          {hemoglobin_g_dl}
        </span>
        , WBC:{" "}
        <span className={getColorClass(wbc_per_ul, "wbc")}>{wbc_per_ul}</span>,{" "}
        Platelet:{" "}
        <span className={getColorClass(platelets_per_ul, "platelets")}>
          {platelets_per_ul}
        </span>
      </span>
    );
  };

  // Helper function to format CRP data with color coding
  const formatCRPData = (data: any) => {
    if (!data) return null;

    const { crp_value, unit } = data;

    const getColorClass = (value: string) => {
      const numValue = parseFloat(value);
      return numValue >= 0.0 && numValue <= 5.0
        ? "text-green-600"
        : "text-red-600";
    };

    return (
      <span className="text-sm">
        Result: <span className={getColorClass(crp_value)}>{crp_value}</span>{" "}
        <span className="text-gray-500">{unit}</span>
      </span>
    );
  };

  // Helper function to format TROP I data with color coding
  const formatTropIData = (data: any) => {
    if (!data) return null;

    const { troponin_i_value, unit } = data;

    const getColorClass = (value: string) => {
      const numValue = parseFloat(value);
      return numValue < 26.0 ? "text-green-600" : "text-red-600";
    };

    return (
      <span className="text-sm">
        Result:{" "}
        <span className={getColorClass(troponin_i_value)}>
          {troponin_i_value}
        </span>{" "}
        <span className="text-gray-500">{unit}</span>
      </span>
    );
  };

  // Helper function to format ECG data
  const formatECGData = (data: any) => {
    if (!data) return null;

    const { ecg_impression } = data;

    return <span className="text-sm">{ecg_impression}</span>;
  };

  // Helper function to sort reports by date
  const getSortedReports = () => {
    if (!reports) return [];

    const allReports: any[] = [];

    Object.entries(reports).forEach(
      ([reportType, reportList]: [string, any]) => {
        if (Array.isArray(reportList)) {
          reportList.forEach((report: any) => {
            allReports.push({
              ...report,
              reportType,
              createdAt: new Date(report.createdAt),
            });
          });
        }
      }
    );

    return allReports.sort((a, b) => {
      if (reportSortOrder === "newest") {
        return b.createdAt.getTime() - a.createdAt.getTime();
      } else {
        return a.createdAt.getTime() - b.createdAt.getTime();
      }
    });
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <div className="px-4 sm:px-6 lg:px-8 py-8 flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Top section: Patient Info and AI Overview side by side */}
        <div className="flex flex-row gap-6 mb-6 flex-shrink-0">
          {/* Patient Details Card */}
          <div className="bg-white rounded-xl shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between px-6 py-4 flex-1 h-[18s0px]">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-xl font-bold text-gray-900">
                  {fullName}
                </span>
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-lg ${riskBadge}`}
                >
                  {riskDisplayText}
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>{age} years</span>
                <span className="mx-1">•</span>
                <span>{gender}</span>
                <span className="mx-1">•</span>
                <span>{height}</span>
                <span className="mx-1">•</span>
                <span>{bloodGroup}</span>
              </div>
              <div className="text-xs text-gray-700 mt-1">
                Contact:{" "}
                <span className="font-bold text-gray-900">{phone}</span>
              </div>
            </div>
            <div className="flex gap-2 mt-3 md:mt-0 md:ml-6">
              <Button
                className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-3 py-1.5 rounded shadow-none border-none text-sm"
                onClick={() => {
                  setEditingNoteId(null);
                  setEditingNoteText("");
                  setNewNote("");
                  setShowNoteModal(true);
                }}
              >
                <StickyNote className="h-4 w-4" />
                <span>Add Note</span>
              </Button>
            </div>
          </div>
          {/* AI Overview Card */}
          <div className="bg-gradient-to-r from-[#fff] to-[#84B9FB] w-[25%] rounded-xl p-[2px]">
            <div className="bg-white rounded-xl shadow-sm px-2 pt-2 pb-4 flex flex-col h-[180px] w-full">
              <div className="flex items-center gap-2 flex-shrink-0">
                <img
                  src="/images/ai_sparkle.png"
                  alt="AI Sparkle"
                  className="w-5 h-5"
                />
                <span className="font-bold text-[#232B3A] text-base">
                  Cardiovascular Risk Summary
                </span>
              </div>
              <hr className="border-t border-gray-200 mb-2 mt-0 flex-shrink-0" />
              {/* AI content goes here */}
              <div className="flex-1 flex flex-col min-h-0">
                {aiLoading ? (
                  <div className="text-sm text-gray-500">
                    Loading summary...
                  </div>
                ) : aiError ? (
                  <div className="text-red-500 text-sm">{aiError}</div>
                ) : (
                  <div className="text-sm text-gray-700 flex-1 flex flex-col min-h-0">
                    <div
                      className="overflow-hidden flex-1"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        lineHeight: "1.4",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: truncateToFourLines(aiSummary),
                      }}
                    />
                    {aiSummary &&
                      aiSummary.replace(/<[^>]*>/g, "").split("\n").length >
                        3 && (
                        <button
                          onClick={() => setShowAiSummarySidebar(true)}
                          className="text-blue-600 text-sm mt-1 hover:text-blue-800 underline self-start flex-shrink-0"
                        >
                          Read more
                        </button>
                      )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Main content: metrics left, chat right */}
        <div className="flex-1 flex flex-row min-h-0 gap-6 overflow-hidden mb-4">
          {/* Left: Health Metrics Dashboard and tabs */}
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 flex-1 flex flex-col min-h-0 overflow-hidden border border-gray-100">
              {/* Tab Options Bar */}
              <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-2 flex-shrink-0">
                <div className="flex flex-wrap gap-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab}
                      className={`flex items-center gap-2 rounded-lg font-medium px-4 py-2 shadow-none border-none text-sm focus:outline-none transition-colors duration-150 ${
                        selectedTab === tab
                          ? "bg-[#F3F4F6] text-gray-900"
                          : "bg-white text-gray-700 hover:bg-[#F3F4F6]"
                      }`}
                      onClick={() => setSelectedTab(tab)}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {/* Filter dropdown - only show for Reports tab */}
                {selectedTab === "Reports" && (
                  <div className="relative" ref={dropdownRef}>
                    <button
                      className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-3 py-1.5 rounded-lg transition-colors duration-150"
                      onClick={() => setShowSortDropdown(!showSortDropdown)}
                    >
                      <span>
                        {reportSortOrder === "newest" ? "Newest" : "Oldest"}
                      </span>
                      <svg
                        className={`w-4 h-4 transition-transform duration-150 ${
                          showSortDropdown ? "rotate-180" : ""
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {showSortDropdown && (
                      <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                        <button
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-t-lg ${
                            reportSortOrder === "newest"
                              ? "bg-blue-50 text-blue-700"
                              : "text-gray-700"
                          }`}
                          onClick={() => {
                            setReportSortOrder("newest");
                            setShowSortDropdown(false);
                          }}
                        >
                          Newest
                        </button>
                        <button
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-b-lg ${
                            reportSortOrder === "oldest"
                              ? "bg-blue-50 text-blue-700"
                              : "text-gray-700"
                          }`}
                          onClick={() => {
                            setReportSortOrder("oldest");
                            setShowSortDropdown(false);
                          }}
                        >
                          Oldest
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto min-h-0 rounded-lg">
                {selectedTab === "Health Metrics" && (
                  <HealthMetricsDashboard
                    vitals={patient.vitals}
                    patientId={id!}
                    heartRateDataForChart={heartRateDataForChart}
                    setHeartRateDataForChart={setHeartRateDataForChart}
                    bloodPressureDataForChart={bloodPressureDataForChart}
                    setBloodPressureDataForChart={setBloodPressureDataForChart}
                    bloodOxygenDataForChart={bloodOxygenDataForChart}
                    setBloodOxygenDataForChart={setBloodOxygenDataForChart}
                    heartRateTimeLabels={heartRateTimeLabels}
                    setHeartRateTimeLabels={setHeartRateTimeLabels}
                    bloodPressureTimeLabels={bloodPressureTimeLabels}
                    setBloodPressureTimeLabels={setBloodPressureTimeLabels}
                    bloodOxygenTimeLabels={bloodOxygenTimeLabels}
                    setBloodOxygenTimeLabels={setBloodOxygenTimeLabels}
                    latestHeartRate={latestHeartRate}
                    setLatestHeartRate={setLatestHeartRate}
                    latestBloodPressure={latestBloodPressure}
                    setLatestBloodPressure={setLatestBloodPressure}
                    latestBloodOxygen={latestBloodOxygen}
                    setLatestBloodOxygen={setLatestBloodOxygen}
                    latestHeartRateTime={latestHeartRateTime}
                    setLatestHeartRateTime={setLatestHeartRateTime}
                    latestBloodPressureTime={latestBloodPressureTime}
                    setLatestBloodPressureTime={setLatestBloodPressureTime}
                    latestBloodOxygenTime={latestBloodOxygenTime}
                    setLatestBloodOxygenTime={setLatestBloodOxygenTime}
                  />
                )}
                {selectedTab === "Medical History" && (
                  <div className="grid grid-cols-3 gap-6 py-4">
                    {/* Symptoms */}
                    <div>
                      <div className="font-semibold text-gray-800 mb-1">
                        Chief Complaint
                      </div>
                      <div className="text-[#F43F5E]">
                        {getQuestionnaireList("symptoms") || (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </div>
                    {/* Health Conditions */}
                    <div>
                      <div className="font-semibold text-gray-800 mb-1">
                        Health Conditions
                      </div>
                      <div className="text-[#F43F5E]">
                        {(() => {
                          const healthConditions =
                            getQuestionnaireList("health_conditions");
                          const diabetes = getQuestionnaireValue("diabetes");
                          const conditions = [];
                          if (healthConditions)
                            conditions.push(healthConditions);
                          if (diabetes) conditions.push(diabetes);
                          return conditions.length > 0 ? (
                            conditions.join(", ")
                          ) : (
                            <span className="text-gray-400">-</span>
                          );
                        })()}
                      </div>
                    </div>
                    {/* Medications */}
                    <div>
                      <div className="font-semibold text-gray-800 mb-1">
                        Medications
                      </div>
                      <div>
                        {getQuestionnaireList("medications") || (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </div>

                    {/* Family Heart History */}
                    <div>
                      <div className="font-semibold text-gray-800 mb-1">
                        Family Heart History
                      </div>
                      <div>
                        {(() => {
                          const value =
                            getQuestionnaireValue("family_history") ||
                            getQuestionnaireValue(
                              "family_history_heart_disease"
                            );
                          return value ? (
                            value.charAt(0).toUpperCase() +
                              value.slice(1).toLowerCase()
                          ) : (
                            <span className="text-gray-400">-</span>
                          );
                        })()}
                      </div>
                    </div>
                    {/* Empty cell for grid alignment */}
                    <div></div>
                  </div>
                )}
                {selectedTab === "Notes" && (
                  <div>
                    {notes.length === 0 ? (
                      <div className="text-gray-400 text-sm mt-4">
                        No notes yet.
                      </div>
                    ) : (
                      notes.map((note) => (
                        <div
                          key={note.id}
                          className="border-b py-3 flex items-start justify-between gap-4"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-gray-500 mb-1">
                              Created:{" "}
                              {note.date.toLocaleDateString(undefined, {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              })}
                              ,{" "}
                              {note.date.toLocaleTimeString(undefined, {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                              {note.updatedAt && (
                                <>
                                  {" • "}
                                  Edited:{" "}
                                  {note.updatedAt.toLocaleDateString(
                                    undefined,
                                    {
                                      day: "2-digit",
                                      month: "long",
                                      year: "numeric",
                                    }
                                  )}
                                  ,{" "}
                                  {note.updatedAt.toLocaleTimeString(
                                    undefined,
                                    { hour: "2-digit", minute: "2-digit" }
                                  )}
                                </>
                              )}
                            </div>
                            <div className="text-gray-800 break-words">
                              {note.text}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() =>
                                note.id && handleEditNote(note.id, note.text)
                              }
                              className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors duration-150"
                              title="Edit note"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() =>
                                note.id && handleDeleteNote(note.id)
                              }
                              className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors duration-150"
                              title="Delete note"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
                {selectedTab === "Reports" && (
                  <div>
                    {reportsLoading ? (
                      <div className="text-gray-400 text-sm mt-4">
                        Loading reports...
                      </div>
                    ) : reportsError ? (
                      <div className="text-red-500 text-sm mt-4">
                        {reportsError}
                      </div>
                    ) : !reports ? (
                      <div className="text-gray-400 text-sm mt-4">
                        No reports available.
                      </div>
                    ) : (
                      <div>
                        {/* Reports list */}
                        {(() => {
                          const sortedReports = getSortedReports();

                          if (sortedReports.length === 0) {
                            return (
                              <div className="text-gray-400 text-sm mt-4">
                                No reports available.
                              </div>
                            );
                          }

                          return sortedReports.map((report: any) => {
                            const analysisKey = `${report.reportType}_${report._id}`;
                            const analysisData = reportAnalysis[analysisKey];

                            // Check if analysis data is directly in the report
                            const reportAnalysisData =
                              report.data || analysisData;

                            return (
                              <div
                                key={report._id}
                                className="border-b py-3 cursor-pointer hover:bg-gray-50 transition-colors duration-150"
                                onClick={() =>
                                  handleReportClick(
                                    report.reportType,
                                    report.fileName
                                  )
                                }
                              >
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-gray-800 font-medium">
                                        {report.reportType
                                          .toUpperCase()
                                          .replace(/_/g, " ")}
                                      </span>
                                      <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                                      {report.reportType === "lipid_profile" &&
                                        reportAnalysisData &&
                                        formatLipidProfileData(
                                          reportAnalysisData
                                        )}
                                      {report.reportType === "cbc" &&
                                        reportAnalysisData &&
                                        formatCBCData(reportAnalysisData)}
                                      {report.reportType === "crp" &&
                                        reportAnalysisData &&
                                        formatCRPData(reportAnalysisData)}
                                      {report.reportType === "trop_i" &&
                                        reportAnalysisData &&
                                        formatTropIData(reportAnalysisData)}
                                      {report.reportType === "ecg" &&
                                        reportAnalysisData &&
                                        formatECGData(reportAnalysisData)}
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-500 ml-4">
                                    {report.createdAt.toLocaleDateString(
                                      undefined,
                                      {
                                        day: "2-digit",
                                        month: "long",
                                        year: "numeric",
                                      }
                                    )}
                                    ,{" "}
                                    {report.createdAt.toLocaleTimeString(
                                      undefined,
                                      {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      }
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Right: Chat Widget */}
          <div className="w-[25%] flex flex-col min-h-0 overflow-hidden">
            <ChatWidget
              patientId={patient.userId}
              doctorId={doctorId}
              onClose={() => {}}
              fullHeight
            />
          </div>
        </div>
      </div>
      {/* Add/Edit Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-4">
              {editingNoteId ? "Edit Note" : "Add Note"}
            </h2>
            <textarea
              className="w-full border rounded p-2 mb-4"
              rows={4}
              value={editingNoteId ? editingNoteText : newNote}
              onChange={(e) =>
                editingNoteId
                  ? setEditingNoteText(e.target.value)
                  : setNewNote(e.target.value)
              }
              placeholder="Enter your note here..."
            />
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded bg-gray-200"
                onClick={() => {
                  setShowNoteModal(false);
                  setEditingNoteId(null);
                  setEditingNoteText("");
                  setNewNote("");
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-blue-600 text-white"
                onClick={handleSaveNote}
              >
                {editingNoteId ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Dialog */}
      {noteToDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-4">Delete Note</h2>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this note? This action cannot be
              undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                onClick={() => setNoteToDelete(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                onClick={confirmDeleteNote}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* AI Summary Sidebar */}
      <>
        {/* Backdrop */}
        <div
          className={`fixed inset-0 bg-black bg-opacity-30 z-30 transition-opacity duration-300 ease-in-out ${
            showAiSummarySidebar
              ? "opacity-100"
              : "opacity-0 pointer-events-none"
          }`}
          onClick={() => setShowAiSummarySidebar(false)}
        />
        {/* Sidebar */}
        <div
          className={`fixed top-0 h-full w-[30%] bg-white shadow-2xl z-50 transform transition-transform duration-500 ease-in-out ${
            showAiSummarySidebar ? "translate-x-0" : "translate-x-full"
          }`}
          style={{ right: 0 }}
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-white">
              <h2 className="text-lg font-bold text-gray-800">
                Cardiovascular Risk Summary
              </h2>
              <button
                onClick={() => setShowAiSummarySidebar(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-200 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div
                className="text-sm text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: aiSummary }}
              />
            </div>
          </div>
        </div>
      </>
      {/* PDF Viewer Modal */}
      {showPdfModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl mx-4 h-[80vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-bold text-gray-800">Report Viewer</h2>
              <button
                onClick={handleClosePdfModal}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="flex-1 p-4">
              {pdfLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading Report...</p>
                  </div>
                </div>
              ) : pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  className="w-full h-full border-0"
                  title="PDF Viewer"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-600">Failed to load Report</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
